// commitments — the derived layer over the Commitment & Stewardship Foundation.
// Spec: docs/design/COMMITMENTS-v0.3.md · authorized in docs/ai-handoff/CHATGPT-RULINGS.md §A
//
// Stored: underlying business facts. Derived here: lifecycle state, stewardship status,
// and health. Never stored, because two columns answering one business question drift
// apart — the deals.status vs deal_stage lesson.
//
// Four corrections from the ruling are load-bearing in this file:
//   1. Stewardship knowledge is its OWN axis. `steward_not_recorded` is not a business
//      state, and `mine` is never returned merely because no steward is recorded.
//   2. Dedupe keys on `projection_key`, not the source object — one Deal or email may
//      legitimately give rise to several commitments.
//   3. Health applies only to ACTIVE commitments; complete/superseded short-circuit it,
//      so an old expected_by can never make a finished commitment "needs attention".
//   4. Unconfirmed AI proposals are not canonical truth (enforced in the read layer).
//
// Pure — no Supabase.

export const COMMITMENT_STATES = ['mine', 'delegated', 'waiting_external', 'complete', 'superseded']
export const COMMITMENT_HEALTH = ['on_track', 'check_in_approaching', 'needs_attention', 'blocked']

/** Domain-sensitive cadence (blueprint §21). An explicit checkpoint_at always wins. */
export const DEFAULT_CADENCE = {
  approachDays: 1,          // how early "check-in approaching" lights up
  graceDays: 0,             // slack after expected_by before it needs attention
  // Z.2 — may a future expected-outcome date support POSITIVE health in this domain?
  // Default false, deliberately: the rule is that a domain must AFFIRMATIVELY permit that
  // judgment, and unknown permission cannot equal yes. Domains opt in; they do not opt out.
  expectedDateJudgesHealth: false,
}

const asDate = (v) => {
  if (!v) return null
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}
const isPast = (v, now) => { const d = asDate(v); return d ? d <= now : false }
const daysUntil = (v, now) => {
  const d = asDate(v)
  return d ? Math.ceil((d - now) / 86400000) : null
}

/** Correction 1 — stewardship knowledge is a separate axis from business state. */
export function deriveStewardshipStatus(c) {
  return c?.steward_user_id ? 'recorded' : 'not_recorded'
}

/**
 * Correction 1 — lifecycle/business state. Returns null when the facts do not support
 * one. NEVER returns 'mine' merely because no steward is recorded.
 */
export function deriveCommitmentState(c) {
  if (!c) return null
  if (c.superseded_at) return 'superseded'
  if (c.completed_at) return 'complete'

  const hasExternal = !!(c.external_holder_id || c.external_holder_label)
  const hasExecutor = !!c.executor_user_id
  const stewardKnown = !!c.steward_user_id

  if (hasExternal) return 'waiting_external'
  if (hasExecutor) {
    // Execution is a fact of its own: Ari can be the confirmed executor while
    // stewardship is still unknown. That is 'delegated' + stewardship not_recorded.
    if (!stewardKnown) return 'delegated'
    return c.executor_user_id === c.steward_user_id ? 'mine' : 'delegated'
  }
  if (stewardKnown) return 'mine'
  return null   // unknown — the facts support no state
}

/**
 * Health — Eighth Gate Z.2.
 *
 * `on_track` is an AFFIRMATIVE JUDGMENT and requires an affirmative timing basis, not
 * merely the absence of a negative signal. The certified Boulder reference deliberately
 * removed "on track" and showed the independent truths instead: STATE waiting external ·
 * ATTENTION no action required now · NEXT CHECKPOINT not set. A calm external wait with
 * nothing scheduled is not evidence that anything is proceeding well — it is evidence
 * that nobody has said when to look again.
 *
 * So a commitment may truthfully be state=waiting_external · ball=counterparty ·
 * actionRequired=false · checkpoint=null · health=null, with no contradiction. The
 * resolver's "No action required now" verdict remains stronger and more precise than any
 * generic positive-health label, and health must never be read as a substitute for it.
 *
 * Health stays independent of lifecycle state, Current Holder, the attention verdict and
 * the checkpoint. Completion and supersession short-circuit it entirely.
 */
export function deriveCommitmentHealth(c, cadence = DEFAULT_CADENCE, now = new Date()) {
  const state = deriveCommitmentState(c)
  if (state === 'complete' || state === 'superseded') return null
  if (!c) return null

  if (c.blocked_at) return 'blocked'

  // CIVIL-DAY comparison, not timestamp arithmetic. expected_by and checkpoint_at are
  // calendar days (TEMPORAL-CONTRACT §2). Comparing them as instants meant a Tuesday
  // checkpoint anchored at UTC noon flipped from approaching to overdue partway through
  // Tuesday for a viewer west of Greenwich — the field changing meaning by the hour when
  // the contract says it only ever meant "look again Tuesday".
  // §1 — "today" is the STEWARD's civil day, not UTC. cadence.timeZone (or a civil-day
  // string passed as `now`) sets the basis; see resolveToday.
  //
  // When the caller states the basis is UNKNOWN, no date-relative verdict may be produced.
  // resolveToday would otherwise fall back to the runtime's own zone, so a commitment whose
  // temporal basis is explicitly unknown could still be judged overdue or on track by
  // whichever machine happened to evaluate it — a machine detail masquerading as business
  // knowledge. `blocked` is unaffected — it returns above, reading a stored fact and
  // comparing no dates, so non-temporal truth survives an unknown basis.
  if (cadence?.temporalBasisKnown === false) return null
  const today = resolveToday({ today: cadence?.today, timeZone: cadence?.timeZone, now })
  const checkpointDay = c.checkpoint_at ? instantToCalendarDate(c.checkpoint_at) : null
  const expectedDay = c.expected_by ? instantToCalendarDate(c.expected_by) : null

  // A promise due TODAY is not yet broken. Overdue begins the day after, plus any grace.
  const expectedDeadline = expectedDay ? addCalendarDays(expectedDay, cadence?.graceDays ?? 0) : null
  if (expectedDeadline && today > expectedDeadline) return 'needs_attention'
  // A checkpoint BEFORE today was missed. A checkpoint TODAY has merely come due.
  if (checkpointDay && checkpointDay < today) return 'needs_attention'
  if (checkpointDay && checkpointDay === today) return 'check_in_approaching'

  if (checkpointDay) {
    const days = calendarDaysBetween(today, checkpointDay)
    if (days <= (cadence?.approachDays ?? 1)) return 'check_in_approaching'
  }

  // The affirmative basis. Everything below is a POSITIVE claim and needs real ground:
  //   · a future checkpoint whose provenance is RECORDED — human, or an approved
  //     domain cadence. A checkpoint with unrecorded provenance is a date nobody has
  //     vouched for, which is not yet a basis for saying the outcome is proceeding.
  //   · a grounded future expected date, and only where the domain affirmatively permits
  //     health to be judged from it.
  // An unclassifiable state can never receive it: a default masquerading as business
  // knowledge is the failure this layer exists to prevent.
  if (state === null) return null
  const groundedProvenance = c.checkpoint_source === 'human' || c.checkpoint_source === 'domain_cadence'
  const hasFutureCheckpoint = !!checkpointDay && checkpointDay > today && groundedProvenance
  const hasFutureExpected = !!expectedDay && !(expectedDeadline && today > expectedDeadline)
    && (cadence?.expectedDateJudgesHealth === true)
  if (hasFutureCheckpoint || hasFutureExpected) return 'on_track'
  return null
}

/**
 * Current Holder — the cross-domain primitive. "Ball" is the Deal-domain RENDERING of
 * this, not a universal ontology: delegation reads "Ari has it", creative reads
 * "Waiting on Disney", scheduling reads "Kat is handling it".
 */
export function deriveCurrentHolder(c, { userName } = {}) {
  if (!c) return { kind: 'unknown', label: null }
  if (c.superseded_at || c.completed_at) return { kind: 'none', label: null }
  if (c.external_holder_id || c.external_holder_label) {
    return { kind: 'external', label: c.external_holder_label || null, id: c.external_holder_id || null }
  }
  if (c.executor_user_id) {
    return { kind: 'internal', label: userName?.(c.executor_user_id) || null, id: c.executor_user_id }
  }
  if (c.steward_user_id) {
    return { kind: 'steward', label: userName?.(c.steward_user_id) || null, id: c.steward_user_id }
  }
  return { kind: 'unknown', label: null }
}

/** Correction 2 — projection identity. One object may raise several commitments. */
export const projectionKeys = {
  dealNegotiation: (dealId) => `deal:${dealId}:negotiation`,
  dealDelivery: (dealId) => `deal:${dealId}:delivery`,
  submissionFollowup: (submissionId) => `submission:${submissionId}:buyer_followup`,
  submissionReader: (submissionId) => `submission:${submissionId}:reader`,
  stageApproval: (projectId, stageId) => `stage:${projectId}:${stageId}:approval`,
  // Intentional Propulsion Slice 3 (issue #140) — the bounded creative-read
  // identity creativeTruth.js's resolveCreativeState() stamps on its shaped
  // output. A deliverable can be VIRTUAL (no expression_deliverables row —
  // deliverableResolve.js's §identity), so this is keyed by the resolver's
  // own entry key, never a database id that may not exist.
  deliverableReview: (projectId, entryKey) => `deliverable:${projectId}:${entryKey}:review`,
}

/** The shape every consumer sees, whether the commitment is stored or projected. */
export function toCommitmentView(c, { cadence, userName, backing = 'stored', now = new Date(), timeZone = null } = {}) {
  const state = deriveCommitmentState(c)
  // "Today" is judged in the STEWARD's civil day. An absent timezone is recorded as
  // unknown rather than silently becoming the viewer's — the reader of this view can then
  // disclose the basis instead of presenting a day-boundary verdict as if it were certain.
  const basisZone = timeZone || cadence?.timeZone || null
  // A basis is known from EITHER an authoritative zone or an explicit civil day. Keying
  // only on the zone made this view disagree with deriveCommitmentHealth called directly:
  // the same commitment and the same explicit `today` yielded needs_attention there and
  // null here. An explicit civil day is precisely what the planning layer passes.
  const explicitDay = typeof cadence?.today === 'string' ? cadence.today : null
  const basisKnown = !!basisZone || !!explicitDay
  const effectiveCadence = {
    ...(cadence || DEFAULT_CADENCE), timeZone: basisZone, temporalBasisKnown: basisKnown,
  }
  return {
    id: c.id || null,
    outcome: c.outcome,
    outcomeSource: c.outcome_source || null,
    description: c.description || null,
    steward: {
      userId: c.steward_user_id || null,
      name: c.steward_user_id ? (userName?.(c.steward_user_id) || null) : null,
      source: c.steward_source || null,
      // The legacy text the identity was migrated FROM ("Aaron", "AB"), kept verbatim.
      // It crossed the storage boundary and stopped at the view: the proposal surface
      // renders "steward evidence: created by <name>", and without this it rendered
      // nothing at all — provenance preserved in the row and lost on the way to the eye.
      sourceValue: c.steward_source_value || null,
      confirmedAt: c.steward_confirmed_at || null,
    },
    stewardshipStatus: deriveStewardshipStatus(c),
    executor: c.executor_user_id
      ? { userId: c.executor_user_id, name: userName?.(c.executor_user_id) || null }
      : null,
    externalHolder: (c.external_holder_id || c.external_holder_label)
      ? { type: c.external_holder_type || null, id: c.external_holder_id || null, label: c.external_holder_label || null }
      : null,
    currentHolder: deriveCurrentHolder(c, { userName }),
    state,
    health: deriveCommitmentHealth(c, effectiveCadence, now),
    todayBasis: {
      timeZone: basisZone,
      source: basisZone ? 'steward' : (explicitDay ? 'explicit' : 'unknown'),
      // No invented runtime day when the basis is unknown.
      day: basisKnown ? resolveToday({ today: explicitDay, timeZone: basisZone, now }) : null,
    },
    expectedBy: c.expected_by || null,
    checkpointAt: c.checkpoint_at || null,
    // §AA.2 — provenance travels with the checkpoint. A derived cadence must stay
    // identifiable as derived; it is not human confirmation.
    checkpointSource: c.checkpoint_source || null,
    blocked: c.blocked_at ? { at: c.blocked_at, reason: c.blocked_reason || null } : null,
    origin: {
      type: c.source_object_type || null,
      id: c.source_object_id || null,
      eventId: c.source_event_id || null,
      evidence: c.source_evidence_refs || [],
    },
    projectionKey: c.projection_key || null,
    links: {
      contacts: c.linked_contacts || [],
      companies: c.linked_companies || [],
      deals: c.linked_deals || [],
      projects: c.linked_projects || [],
      properties: c.linked_properties || [],
      submissions: c.linked_submissions || [],
      calendarEvents: c.linked_calendar_events || [],
    },
    provenance: c.provenance || null,
    confidence: c.confidence || null,
    humanConfirmed: !!c.human_confirmed,
    lastEvidenceAt: c.last_evidence_at || null,
    fulfillingTaskId: c.fulfilling_task_id || null,
    backing,
  }
}

/**
 * Correction 2 — a stored commitment suppresses ONLY the derived projection whose
 * projection_key it matches. A stored row with no projection_key suppresses nothing.
 */
export function mergeStoredAndProjected(stored = [], projected = []) {
  const claimed = new Set(stored.map(s => s.projectionKey).filter(Boolean))
  return [...stored, ...projected.filter(p => !claimed.has(p.projectionKey))]
}

/**
 * How to SAY who holds this, for a particular reader in a particular domain.
 *
 * Two corrections live here:
 *
 *  · "Yours" is viewer-relative. `state === 'mine'` means the steward holds it, which is
 *    only "yours" to the steward. Rendering it as "Yours" to anyone else tells Kat that
 *    Aaron's commitment is hers — the system inventing ownership, which the ruling
 *    forbids outright.
 *
 *  · "Ball" is the DEAL-domain rendering of Current Holder, not the universal word. A
 *    submission is not waiting on a ball; a creative stage is not either. Outside deals
 *    the cross-domain primitive is spoken plainly.
 */
export function describeHolder(commitment, { viewerUserId = null, domain = null } = {}) {
  if (!commitment) return { term: 'Current holder', label: null, note: null }
  const term = domain === 'deal' ? 'Ball with' : 'Currently with'
  const { currentHolder: h, state, steward, executor } = commitment

  if (state === 'complete') return { term, label: 'No one — complete', note: null }
  if (state === 'superseded') return { term, label: 'No one — superseded', note: null }

  if (h?.kind === 'external') {
    return { term, label: h.label || 'the other side', note: 'Waiting externally' }
  }
  if (h?.kind === 'internal') {
    const mine = executor?.userId && executor.userId === viewerUserId
    return {
      term,
      label: mine ? 'You' : (executor?.name || h.label || 'someone internal'),
      note: steward?.name ? `Delegated by ${steward.name}` : 'Delegated',
    }
  }
  if (h?.kind === 'steward') {
    const mine = steward?.userId && steward.userId === viewerUserId
    return { term, label: mine ? 'You' : (steward?.name || 'the steward'), note: null }
  }
  // Unknown is an answer, not a blank — and never defaults to the reader.
  return { term, label: null, note: 'The record does not establish whose move this is' }
}

/** Viewer-relative one-word state, for chips and lists. */
export function describeState(commitment, { viewerUserId = null } = {}) {
  const state = commitment?.state
  if (!state) return null
  if (state === 'mine') {
    const mine = commitment.steward?.userId && commitment.steward.userId === viewerUserId
    return mine ? 'Yours' : `${commitment.steward?.name || 'Steward'}'s`
  }
  return { delegated: 'Delegated', waiting_external: 'Waiting externally',
           complete: 'Complete', superseded: 'Superseded' }[state] || state
}

// ── Calendar dates ────────────────────────────────────────────────────────────────────
//
// `expected_by` answers "on what DAY should this outcome happen" — a calendar date, not an
// instant. Stored in a timestamptz column, so the conversion has to be pinned or the day
// moves: `new Date('2026-08-20').toISOString()` parses as UTC MIDNIGHT, and every timezone
// west of Greenwich then renders it as the 19th. A deadline that reads a day early is
// worse than no deadline, because it looks authoritative.
//
// Two halves, and BOTH are needed — the anchor alone is not enough:
//
//   1. Write at UTC noon, so the stored instant is unambiguous and round-trips back to the
//      same 'YYYY-MM-DD' the user picked. (Same fix used for stated calendar dates in
//      quotedMessages.js.) Under raw local rendering this holds from UTC-12 to UTC+11 —
//      but NOT at UTC+12/+13, where noon UTC is already the next day. Auckland proves it.
//   2. Read back pinned to UTC, which is what actually makes the day stable in EVERY
//      timezone rather than merely most of them.
//
// Chatrone runs US and Brazil today, both west of Greenwich, so the anchor alone would
// have passed unnoticed here. That is precisely why the display is pinned too.
const CALENDAR_ANCHOR_HOUR = 12

/**
 * The civil day it currently is FOR SOMEBODY.
 *
 * A calendar promise is timezone-independent — "Aug 20" is Aug 20 for everyone — but the
 * word "today" cannot be. Deriving it with instantToCalendarDate(now) read UTC, so at 7pm
 * Monday in Los Angeles it was already Tuesday: a Monday deadline expired five hours
 * before Monday ended, and late Sunday fell into the next planning week before the steward
 * had reached Monday.
 *
 * Callers pass the relevant steward's timezone at the boundary. The fallback is the
 * runtime's own zone rather than UTC — a wrong-but-local guess is visible to whoever runs
 * it, whereas UTC was silently wrong for every Chatrone user, all of whom are west of it.
 */
export function civilDayIn(timeZone, instant = new Date()) {
  const d = instant instanceof Date ? instant : new Date(instant)
  // en-CA renders ISO-ordered Y-M-D, so no reassembly is needed.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone || undefined, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d)
}

/**
 * Whether a stored string is a real IANA zone `Intl` can resolve — never
 * throws. A stewardship read (Studio steward timezone, issue #148) must never
 * hand a blank/garbage `user_profiles.timezone` value to `civilDayIn`/
 * `resolveToday`: an empty string there silently falls back to the
 * EVALUATING RUNTIME's own zone (the exact defect this whole temporal-basis
 * line of work exists to close), and a genuinely malformed IANA name throws
 * a RangeError. Both must read as "no usable basis", never as a guess and
 * never as an unhandled exception.
 */
export function isValidTimeZone(tz) {
  if (!tz || typeof tz !== 'string') return false
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat('en-CA', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

// Offset (ms, UTC minus local) of an IANA timezone at a given UTC instant —
// same guess-then-correct technique icsParse.js's private tzOffsetMs already
// uses for the identical reason (a DST transition can make the first guess
// wrong by exactly its own shift). Kept private: nothing outside
// startOfCivilDayUTC needs the raw offset.
function tzOffsetMsAt(timeZone, utcMs) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
    const parts = {}
    for (const { type, value } of dtf.formatToParts(new Date(utcMs))) parts[type] = value
    const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
    return asUTC - utcMs
  } catch {
    return null
  }
}

/**
 * The UTC instant of LOCAL MIDNIGHT for `dayStr` ('YYYY-MM-DD') in
 * `timeZone` — the inverse of civilDayIn: turns a civil day back into a
 * precise instant, so a caller can test a half-open interval against a
 * viewer's own day boundary instead of comparing civil-day STRINGS (which
 * can't express "an event ends exactly at this day's midnight" — see
 * dailyAgenda.js's timedSpans, and useGoogleCalendar.js's fetch range,
 * which anchors to this rather than the runtime's own midnight so a
 * viewer's authoritative civil day is what gets fetched, not the browser's).
 * A falsy/invalid `timeZone` falls back to the runtime's own zone — the same
 * posture civilDayIn itself takes for a falsy zone, never a silent UTC
 * guess. Never throws; an unparseable `dayStr` returns null.
 */
export function startOfCivilDayUTC(timeZone, dayStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayStr || '')
  if (!m) return null
  const [, y, mo, d] = m
  const naive = Date.UTC(+y, +mo - 1, +d, 0, 0, 0)
  const zone = timeZone || undefined
  let off = tzOffsetMsAt(zone, naive)
  if (off === null) return new Date(naive)
  off = tzOffsetMsAt(zone, naive - off) ?? off
  return new Date(naive - off)
}

/** Resolve "today" from an explicit civil day, a timezone, or the runtime's own zone. */
export function resolveToday({ today = null, timeZone = null, now = new Date() } = {}) {
  if (typeof today === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(today)) return today
  if (typeof now === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(now)) return now
  return civilDayIn(timeZone, now)
}

/** Civil-day arithmetic on 'YYYY-MM-DD', free of timezone drift. */
export function addCalendarDays(isoDay, n) {
  const d = new Date(`${isoDay}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** Whole civil days from `a` to `b` (negative when b precedes a). */
export function calendarDaysBetween(a, b) {
  return Math.round((new Date(`${b}T12:00:00Z`) - new Date(`${a}T12:00:00Z`)) / 86400000)
}

/** 'YYYY-MM-DD' (from a date input) → an instant that renders as that day everywhere. */
export function calendarDateToInstant(value) {
  if (!value) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim())
  if (!m) {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d.toISOString()
  }
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], CALENDAR_ANCHOR_HOUR)).toISOString()
}

/** An instant → 'YYYY-MM-DD' for a date input, read in UTC so the anchor round-trips. */
export function instantToCalendarDate(value) {
  if (!value) return ''
  const d = new Date(value)
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

/**
 * Display a CALENDAR-DATE field (expected_by). Formatted in UTC deliberately: the value
 * was anchored at UTC noon to mean a specific DAY, so reading it back in the viewer's zone
 * would reintroduce the drift this exists to prevent.
 *
 * Do NOT use this for a computed instant — see formatInstantDay below.
 */
export function formatCalendarDate(value, opts = { month: 'short', day: 'numeric' }) {
  if (!value) return null
  const d = new Date(value)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { ...opts, timeZone: 'UTC' })
}

/**
 * Display the day of a genuine INSTANT (checkpoint_at, last_evidence_at) in the viewer's
 * own timezone.
 *
 * Use it for a value that IS an instant. Today the only such producer is the deal
 * projection's `checkpoint_at`, which is `new Date(now)` — the moment the resolver decided
 * a follow-up is warranted. UTC-pinning that displayed a checkpoint meaning "now" as
 * TOMORROW for anyone west of Greenwich; the live browser pass showed "Look again: Aug 18"
 * at 7pm on Aug 17 in Los Angeles.
 *
 * IMPORTANT — this does NOT establish that `checkpoint_at` is universally an instant.
 * "Look again on Tuesday" is a calendar day wearing the same column, and which meaning
 * governs per field, per domain, and in which authoritative timezone is settled by the
 * Eighth Gate AA.4 date-semantics contract — not here. Until that contract lands, this
 * helper is applied to the producers known to emit instants, and nothing wider.
 *
 * The lesson is narrow and worth keeping: the calendar-date fix was correct for the field
 * it was written for, and wrong the moment it was applied to a field of a different kind.
 * Over-generalizing the correction is the same mistake in the other direction.
 */
export function formatInstantDay(value, opts = { month: 'short', day: 'numeric' }) {
  if (!value) return null
  const d = new Date(value)
  return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, opts)
}

/** Do two instants fall on the same calendar day, as the writer meant it (UTC-anchored)? */
export function isSameCalendarDay(a, b) {
  return instantToCalendarDate(a) === instantToCalendarDate(b)
}
